import { useRef } from "react";
import { Upload, Download, Loader2, X } from "lucide-react";

const VALID_ISSUES = ["lost_key", "not_working", "locked_inside", "key_copy"];

export function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { items: [], errors: ["El CSV está vacío"] };
  const separator = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
  const parseRow = (line) => line.split(separator).map((c) => c.trim().replace(/^"|"$/g, ""));
  const header = parseRow(lines[0]).map((h) => h.toLowerCase());
  const required = ["brand", "model", "year_from", "year_to", "issue_type", "price"];
  const missing = required.filter((f) => !header.includes(f));
  if (missing.length) return { items: [], errors: [`Faltan columnas: ${missing.join(", ")}`] };
  const idx = Object.fromEntries(required.map((f) => [f, header.indexOf(f)]));
  const items = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseRow(lines[i]);
    const row = {
      brand: cols[idx.brand] || "",
      model: cols[idx.model] || "",
      year_from: parseInt(cols[idx.year_from], 10),
      year_to: parseInt(cols[idx.year_to], 10),
      issue_type: (cols[idx.issue_type] || "").trim().toLowerCase(),
      price: parseFloat((cols[idx.price] || "0").replace(",", ".")),
    };
    if (!row.brand || !row.model) { errors.push(`Fila ${i + 1}: marca/modelo vacíos`); continue; }
    if (!Number.isInteger(row.year_from) || !Number.isInteger(row.year_to)) { errors.push(`Fila ${i + 1}: años inválidos`); continue; }
    if (row.year_from > row.year_to) { errors.push(`Fila ${i + 1}: año_desde > año_hasta`); continue; }
    if (!VALID_ISSUES.includes(row.issue_type)) { errors.push(`Fila ${i + 1}: issue_type "${row.issue_type}" inválido (usa: ${VALID_ISSUES.join("|")})`); continue; }
    if (isNaN(row.price) || row.price < 0) { errors.push(`Fila ${i + 1}: precio inválido`); continue; }
    items.push(row);
  }
  return { items, errors };
}

export function downloadCsvTemplate() {
  const csv = "brand,model,year_from,year_to,issue_type,price\nVolkswagen,Golf,2015,2020,lost_key,280\nSeat,Ibiza,2010,2017,lost_key,220\nBMW,Serie 3,2016,2022,not_working,180\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "precios-vehiculos-plantilla.csv";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function VehiclePriceImport({ open, onClose, importText, setImportText, importing, importResult, setImportResult, onImport }) {
  const fileInputRef = useRef(null);
  if (!open) return null;

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setImportText(text);
  };

  const close = () => { onClose(); setImportResult(null); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="import-overlay">
      <div className="bg-[#0A0A0A] border border-white/10 rounded-md p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-display font-bold text-xl">Importar precios desde CSV</h4>
          <button onClick={close} className="text-[#71717A] hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="mb-4 p-3 rounded-md bg-[#FFC107]/5 border border-[#FFC107]/20 text-xs text-[#A1A1AA] leading-relaxed">
          <div className="font-mono uppercase tracking-wider text-[#FFC107] text-[10px] mb-1">Formato esperado (con cabecera)</div>
          <code className="block font-mono text-[11px] text-white/80">brand,model,year_from,year_to,issue_type,price</code>
          <div className="mt-2">
            <b>issue_type</b> debe ser uno de: <code className="text-white">lost_key</code>, <code className="text-white">not_working</code>, <code className="text-white">locked_inside</code>, <code className="text-white">key_copy</code>.
            Precio en euros, <b>sin IVA</b>. Separador aceptado: coma o punto y coma.
          </div>
          <button onClick={downloadCsvTemplate} className="mt-2 text-[#FFC107] hover:underline text-xs flex items-center gap-1" data-testid="download-template-btn">
            <Download className="w-3 h-3" /> Descargar plantilla de ejemplo
          </button>
        </div>

        <div className="flex gap-2 mb-3">
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFilePick} className="hidden" data-testid="csv-file-input" />
          <button onClick={() => fileInputRef.current?.click()} className="btn-outline px-4 py-2 rounded-md text-sm font-medium flex items-center gap-1" data-testid="csv-pick-btn">
            <Upload className="w-3.5 h-3.5" /> Seleccionar archivo CSV
          </button>
          <button onClick={() => setImportText("")} className="text-xs text-[#71717A] hover:text-white px-2" data-testid="csv-clear-btn">Limpiar</button>
        </div>

        <textarea
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder={"brand,model,year_from,year_to,issue_type,price\nVolkswagen,Golf,2015,2020,lost_key,280\n..."}
          rows={10}
          className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-xs font-mono text-white focus:border-[#FFC107] outline-none"
          data-testid="csv-textarea"
        />

        {importResult && (
          <div className="mt-4 p-3 rounded-md bg-[#141414] border border-white/10" data-testid="import-result">
            <div className="flex items-center gap-3 text-sm mb-2">
              <span className="text-[#10B981] font-semibold">✓ {importResult.created} creados</span>
              {importResult.errors.length > 0 && <span className="text-[#EF4444] font-semibold">✗ {importResult.errors.length} errores</span>}
              <span className="text-[#71717A] ml-auto text-xs">Total: {importResult.total}</span>
            </div>
            {importResult.errors.length > 0 && (
              <ul className="text-xs text-[#EF4444] space-y-1 max-h-32 overflow-y-auto">
                {importResult.errors.slice(0, 20).map((e, i) => (
                  <li key={`${e.row}-${e.brand || ""}-${e.model || ""}-${i}`}>· Fila {e.row}: {e.error}{e.brand ? ` (${e.brand} ${e.model})` : ""}</li>
                ))}
                {importResult.errors.length > 20 && <li>… y {importResult.errors.length - 20} más</li>}
              </ul>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button onClick={close} className="btn-outline flex-1 px-4 py-2.5 rounded-md text-sm font-medium" data-testid="import-cancel-btn">Cerrar</button>
          <button onClick={onImport} disabled={importing || !importText.trim()} className="btn-amber flex-1 px-4 py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="import-run-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}
